import React from "react";
import { Switch, Redirect, Route, RouteProps } from "react-router-dom";

import routes, { rulesByRoute, RouteKey } from "lib/routes";

import SessionContext from "context/session";
import CountryContext from "context/country";

import { LoadingSpinner } from "components/ProgressIndicator";

import * as pages from "pages";

type AsyncRouteProps = RouteProps & {
  loading: boolean;
  name: RouteKey;
};

const SessionAwareRoute = ({
  loading,
  name,
  ...routeProps
}: AsyncRouteProps) => {
  const { authenticated, currentUser } = React.useContext(SessionContext);
  const signupEmailVerified = !!currentUser?.signupEmailVerified;
  const rules = rulesByRoute[name];
  const accessible = rules.isAccessible(authenticated, signupEmailVerified);

  if (!accessible) {
    const redirectTo = !authenticated
      ? routes.setup
      : !signupEmailVerified
      ? routes.setupCheckEmail
      : rules.fallback;

    return <Redirect to={redirectTo} />;
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return <Route {...routeProps} />;
};

const MainContentRouter = () => {
  const { ready: countriesReady } = React.useContext(CountryContext);
  const { ready: sessionReady } = React.useContext(SessionContext);

  const setupLoading = !countriesReady || !sessionReady;
  const loading = !sessionReady;

  return (
    <Switch>
      <SessionAwareRoute
        exact
        path={routes.setup}
        component={pages.Setup}
        name="setup"
        loading={setupLoading}
      />

      <SessionAwareRoute
        exact
        path={routes.setupCheckEmail}
        component={pages.SetupCheckEmail}
        name="setupCheckEmail"
        loading={loading}
      />

      <Route exact path={routes.confirmEmail} component={pages.ConfirmEmail} />

      <SessionAwareRoute
        exact
        name="reservation"
        path={routes.reservation}
        component={pages.Reservation}
        loading={loading}
      />

      <SessionAwareRoute
        exact
        name="customization"
        path={routes.customization}
        component={pages.Customization}
        loading={loading}
      />

      <SessionAwareRoute
        exact
        name="team"
        path={routes.team}
        component={pages.Team}
        loading={loading}
      />

      <SessionAwareRoute
        exact
        name="billing"
        path={routes.billing}
        component={pages.Billing}
        loading={loading}
      />

      <Redirect path="*" to={routes.setup} />
    </Switch>
  );
};

export default MainContentRouter;
